import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Alert, Button, Card, CardContent, Container, Stack, TextField, Typography } from '@mui/material';
import Navbar from '../../setup/Navbar';
import BakerySaleBuilder from './BakerySaleBuilder';
import { getForwardInvoice, printForwardInvoice } from '../../../services/bakeryPosApi';
import { fetchPosOrderTypeSettings } from '../../../utils/posOrderTypeSettings';

export default function BakeryForwardInvoice() {
  const { user } = useSelector((state) => state.auth);
  const [invoice, setInvoice] = useState(null);
  const [editLookup, setEditLookup] = useState('');
  const [editInvoice, setEditInvoice] = useState(null);
  const [printMessage, setPrintMessage] = useState('');
  const [printing, setPrinting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const editScanInputRef = useRef(null);

  useEffect(() => {
    const handleEditScanShortcut = (event) => {
      if (event.key !== 'F2' || event.repeat) return;
      event.preventDefault();
      editScanInputRef.current?.focus();
      editScanInputRef.current?.select();
    };
    window.addEventListener('keydown', handleEditScanShortcut);
    return () => window.removeEventListener('keydown', handleEditScanShortcut);
  }, []);
  const handleNetworkPrint = async (tokenNo) => {
    setPrinting(true);
    setPrintMessage('');
    try {
      const result = await printForwardInvoice(tokenNo);
      setPrintMessage(result.message || 'Forward invoice printed.');
    } catch (error) {
      setPrintMessage(error?.response?.data?.message || 'Network printer par print nahi ho saka.');
    } finally {
      setPrinting(false);
    }
  };

  const handleInvoiceCreated = async (createdInvoice, { printAfterSave = false } = {}) => {
    setActionMessage('');
    setInvoice(createdInvoice);
    if (printAfterSave) {
      await handleNetworkPrint(createdInvoice.tokenNo);
      return;
    }
    try {
      const settings = await fetchPosOrderTypeSettings();
      const branchID = createdInvoice.branchID || user?.defaultBranchID || user?.branchID;
      const assignmentResponse = branchID
        ? await axios.get(`${global.SET_API_URL}posOrderTypeSettings/forward-printers`, { params: { branchID } })
        : null;
      const assignment = (assignmentResponse?.data?.data || []).find((row) => Number(row.userID) === Number(createdInvoice.salesmanUserID || user?.userID));
      const shouldAutoPrint = assignment ? Boolean(assignment.autoPrint) : (settings.autoPrintForwardInvoice && settings.forwardInvoicePrinterIP);
      if (shouldAutoPrint) {
        await handleNetworkPrint(createdInvoice.tokenNo);
      }
    } catch (_error) {
      setPrintMessage('Invoice create ho gaya, lekin auto-print settings load nahi ho sakin.');
    }
  };
  const handleScannedEdit = async () => {
    const lookup = editLookup.trim();
    if (!lookup || loadingEdit) return;
    setLoadingEdit(true);
    setActionMessage('');
    try {
      const scannedInvoice = await getForwardInvoice(lookup);
      if (Number(scannedInvoice.salesmanUserID) !== Number(user?.userID)) {
        setActionMessage('Aap sirf apni banayi hui forward invoice edit kar sakte hain.');
        return;
      }
      if (scannedInvoice.status !== 'unpaid') {
        setActionMessage(`Sirf unpaid invoice edit ho sakti hai. Ye invoice ${scannedInvoice.status} hai.`);
        return;
      }
      setEditInvoice(scannedInvoice);
      setInvoice(null);
      setActionMessage('Forward invoice editing ke liye load ho gayi.');
    } catch (error) {
      setActionMessage(error?.response?.data?.message || 'Forward invoice nahi mili.');
    } finally {
      setLoadingEdit(false);
    }
  };
  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
          <Typography variant="h4" fontWeight={800}>Bakery Forward Invoice</Typography>
          <Typography fontWeight={700} color="primary">F1 Search | F2 Edit Scan | F3 Item Scan | {editInvoice ? 'F4 Update & Print' : 'F4 Save & Print'}</Typography>
        </Stack>
        {actionMessage && <Alert severity={actionMessage.includes('load ho gayi') ? 'success' : 'error'} sx={{ mb: 2 }}>{actionMessage}</Alert>}
        {invoice && (
          <Card variant="outlined" sx={{ mb: 2 }} className="bakery-forward-token">
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                {invoice.qrCodeDataUrl && <img src={invoice.qrCodeDataUrl} alt={invoice.tokenNo} width="150" height="150" />}
                <Stack spacing={0.5}>
                  <Alert severity="success">Forward invoice successfully created</Alert>
                  <Typography variant="h5" fontWeight={900}>{invoice.tokenNo}</Typography>
                  <Typography>Total: Rs. {Number(invoice.grandTotal).toLocaleString()}</Typography>
                  <Typography>Status: {String(invoice.status).toUpperCase()}</Typography>
                  {printMessage && <Alert severity={printMessage.includes('Printed') ? 'success' : 'info'}>{printMessage}</Alert>}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant="contained" disabled={printing} onClick={() => handleNetworkPrint(invoice.tokenNo)}>
                      {printing ? 'Printing...' : 'Print on Network Printer'}
                    </Button>
                    <Button variant="outlined" onClick={() => window.print()}>Browser Print Fallback</Button>
                    <Button variant="outlined" disabled={loadingEdit || printing} onClick={() => { setEditInvoice(invoice); setInvoice(null); }}>
                      Edit Invoice
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={800}>Scan Invoice to Edit</Typography>
            <Typography color="text.secondary" sx={{ mb: 1.5 }}>Apni unpaid forward invoice ka barcode scan karein ya token number enter karein.</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField autoFocus fullWidth size="small" label="Scan barcode / token number" value={editLookup}
                inputRef={editScanInputRef}
                onChange={(event) => setEditLookup(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); handleScannedEdit(); } }} />
              <Button variant="contained" disabled={!editLookup.trim() || loadingEdit} onClick={handleScannedEdit}>
                {loadingEdit ? 'Loading...' : 'Edit Invoice'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
        <BakerySaleBuilder key={editInvoice?.forwardInvoiceID || 'new'} mode="forward" editInvoice={editInvoice} onCancelEdit={() => setEditInvoice(null)}
          onCompleted={async (savedInvoice, options) => {
            await handleInvoiceCreated(savedInvoice, options);
            setEditInvoice(null);
            setEditLookup('');
          }} />
      </Container>
    </>
  );
}
